// src/components/settings/shared/dialogs/PetSizeDialog.tsx

import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import DialogBase from '../../../common/modal/DialogBase';
import {
  StyledTextField,
  StyledSwitch,
  DailogLabel,
  DialogLabelText,
} from '../../../forms/shared/FormElements';
import FieldHelperText from '../../../forms/shared/FieldHelperText';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import CustomSelect from '../../../common/CustomSelect';
import CustomSelectMenuItem from '../../../common/CustomSelectMenuItem';
import { PetSize, PetType } from '../../../../services/api';

interface PetSizeFormData {
  size: string;
  petTypeFk: number;
  active: boolean;
}

const EMPTY_FORM: PetSizeFormData = {
  size: '',
  petTypeFk: 0,
  active: true,
};

interface PetSizeDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: PetSizeFormData) => void;
  editing: PetSize | null;
  petTypes: PetType[];
}

const PetSizeDialog: React.FC<PetSizeDialogProps> = ({
  open,
  onClose,
  onSave,
  editing,
  petTypes,
}) => {
  const [formData, setFormData] = useState<PetSizeFormData>(EMPTY_FORM);

  useEffect(() => {
    if (open) {
      setFormData(
        editing
          ? {
              size: editing.size,
              petTypeFk: editing.petTypeFk,
              active: editing.active ?? true,
            }
          : EMPTY_FORM
      );
    }
  }, [open, editing]);

  const canSave = formData.size.trim().length > 0 && formData.petTypeFk > 0;
  const isSystem = !!editing?.isSystem;

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Pet Size' : 'Add Pet Size'}
      headerIcon={editing ? <EditIcon /> : <AddIcon />}
      maxWidth="sm"
      draggable
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={() => onSave(formData)}
            disabled={!canSave}
            text={editing ? 'Save' : 'Save'}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={2} mt={2}>
        <DailogLabel>
          <DialogLabelText id="pet-size-name-label">Size Name:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          id="pet-size-name-input"
          inputProps={{ 'aria-labelledby': 'pet-size-name-label' }}
          value={formData.size}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, size: e.target.value })
          }
          required
          fullWidth
          disabled={isSystem}
        />
        <DailogLabel>
          <DialogLabelText>Pet Type:</DialogLabelText>
        </DailogLabel>
        <CustomSelect
          value={formData.petTypeFk ? String(formData.petTypeFk) : ''}
          onChange={(value) =>
            setFormData({ ...formData, petTypeFk: Number(value) })
          }
          ariaLabel="Pet Type"
          required
          disabled={isSystem}
        >
          {petTypes.map((pt) => (
            <CustomSelectMenuItem key={pt.id} value={String(pt.id)}>
              {pt.type}
            </CustomSelectMenuItem>
          ))}
        </CustomSelect>
        {isSystem && (
          <FieldHelperText helperText="Standard system item - name and pet type cannot be changed. You can still deactivate it below." />
        )}
        {editing && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
              Active
            </Typography>
            <StyledSwitch
              checked={formData.active}
              onChange={(e) =>
                setFormData({ ...formData, active: e.target.checked })
              }
            />
          </Box>
        )}
      </Box>
    </DialogBase>
  );
};

export default PetSizeDialog;
